import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import { type GraphQLTaggedNode } from 'react-relay';
import {
	createOperationDescriptor,
	type ConcreteRequest,
	getRequest,
	type RecordSourceProxy,
	type RecordProxy,
	type ReaderSelection,
	type ReaderLinkedField,
	type ReaderScalarField,
} from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import {
	syncQueryCacheBridge,
	type QueryCacheBridgeSource,
} from './cacheBridge/queryCacheBridgeSync.ts';
import { getRelayFieldStorageKey } from './relayStorageKeys.ts';

export type WriteQueryInteropArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<DataProxy.WriteQueryOptions<TData, TVariables>, 'data' | 'variables'> & {
	query: GraphQLTaggedNode;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function writeQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): void {
	const source: QueryCacheBridgeSource = isRelayEnabled(params.__callerPackage__)
		? 'relay'
		: 'apollo';

	const didWrite =
		source === 'relay'
			? writeQueryInteropRelay<TData, TVariables>(params)
			: writeQueryInteropApollo<TData, TVariables>(params);

	if (didWrite) {
		syncQueryCacheBridge({
			source,
			query: params.query,
			variables: params.variables,
			data: params.data,
		});
	}
}

function writeQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: WriteQueryInteropArgs<TData, TVariables>,
): boolean {
	// Guard window for SSR; this only runs client-side.
	const relayEnv = typeof window !== 'undefined' ? window.GLOBAL_RELAY_ENV : undefined;
	const { query, data, variables } = params;

	if (!relayEnv || !data) {
		return false;
	}

	let didWrite = false;
	relayEnv.commitUpdate((storeProxy: RecordSourceProxy) => {
		const root = storeProxy.getRoot();
		if (!root) {
			return;
		}

		const queryNode = query as ConcreteRequest;
		const queryFields = queryNode?.operation?.selections;

		if (queryFields) {
			didWrite = writeQueryFields(
				root,
				queryFields as readonly ReaderSelection[],
				data,
				variables || {},
			);
		}
	});

	if (!didWrite) {
		// If no existing Relay records were updated, fall back to Relay's normal payload
		// write path. Invalid Relay artifacts should still throw here, preserving the
		// original writeQueryInterop failure behavior.
		const operation = createOperationDescriptor(getRequest(query), variables ?? {});
		relayEnv.commitPayload(operation, data as Record<string, unknown>);
		didWrite = true;
	}

	return didWrite;
}

// Bounded recursive walk of the query selection tree (mirrors readFields in readQuery.ts);
// the rule's recursion/iteration heuristic is a false positive here.
/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
function writeQueryFields(
	record: RecordProxy,
	fields: readonly ReaderSelection[],
	data: any,
	variables: Record<string, any>,
): boolean {
	if (!record || !data) {
		return false;
	}

	let didWrite = false;
	for (const field of fields) {
		if (field.kind === 'Condition') {
			// Honour @include/@skip so conditional fields (e.g. userPreferences) get written.
			const conditionValue = variables[field.condition];
			const shouldInclude = field.passingValue ? conditionValue : !conditionValue;
			if (shouldInclude) {
				didWrite = writeQueryFields(record, field.selections, data, variables) || didWrite;
			}
		} else if (field.kind === 'InlineFragment') {
			didWrite = writeQueryFields(record, field.selections, data, variables) || didWrite;
		} else if (field.kind === 'LinkedField') {
			const linkedField = field as ReaderLinkedField;
			const fieldData = data[linkedField.name];
			if (fieldData == null || !linkedField.selections) {
				continue;
			}

			const fieldKey = getRelayFieldStorageKey(linkedField, variables);

			if (linkedField.plural) {
				if (!Array.isArray(fieldData)) {
					continue;
				}
				// Read plural fields with getLinkedRecords; the singular getter throws on
				// plural data. Existing records are updated in place, index-aligned with
				// fieldData (creating new records is out of scope — see singular branch).
				const existingRecords = record.getLinkedRecords(fieldKey);
				if (!existingRecords) {
					continue;
				}
				for (let index = 0; index < fieldData.length; index++) {
					const item = fieldData[index];
					const existingRecord = existingRecords[index];
					if (existingRecord && item && typeof item === 'object') {
						didWrite =
							writeQueryFields(existingRecord, linkedField.selections, item, variables) || didWrite;
					}
				}
			} else if (typeof fieldData === 'object') {
				const linkedRecord = record.getLinkedRecord(fieldKey);

				// Only update existing records; creating new ones is out of scope.
				if (linkedRecord) {
					didWrite =
						writeQueryFields(linkedRecord, linkedField.selections, fieldData, variables) ||
						didWrite;
				}
			}
		} else if (field.kind === 'ScalarField') {
			const scalarField = field as ReaderScalarField;
			if (data[scalarField.name] !== undefined) {
				record.setValue(data[scalarField.name], getRelayFieldStorageKey(scalarField, variables));
				didWrite = true;
			}
		}
	}

	return didWrite;
}
/* eslint-enable @atlassian/perf-linting/no-quadratic-complexity */

function writeQueryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): boolean {
	window['GLOBAL_APOLLO_CLIENT']!.writeQuery({
		...params,
		query: toDocumentNode(params.query),
	});
	return true;
}
